import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useId
} from "react";
import {
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  Collapse,
  Fade,
  Grid2,
  LinearProgress,
  Stack,
  Typography
} from "@mui/material";
import { ExpandMore, FilterList } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { inputsReady } from "../../../components/BookingForm/functions/form.functions";
import { GroupFiltersSub } from "../../../components/BookingForm/BookingFilter/GroupFiltersSub.partial";
import {
  GroupFilterSelected,
  GroupFilterType
} from "../../../components/BookingForm/BookingFilter/types/GroupFilters.type";
import { BookingFilterMode } from "../../../components/BookingForm/Domain/booking-filter-mode-enum.type";
import { zoneMode, placeModeExcludeVehicle } from "../functions/booking-form.functions";
import {
  buildBookingFiltersFromGroupFilterItems,
  countActiveBookingFilterSelections,
  countActiveGroupFilterItems,
  groupFilterItemsFromBookingFilters,
  hasActiveBookingFilters,
  shouldShowEarlyInventoryFilters
} from "../functions/booking-filter-state.functions";
import {
  setBookingFilters,
  setUserBookingFilters,
  clearBookingFilters
} from "../slices/booking.slice";
import { BookingType, NotSerializedBookingInputs } from "../typings/booking-inputs";
import { useRemoteFetchBookingInventoryFilterOptions } from "../../../hooks/Remote/Booking/useRemoteFetchBookingInventoryFilterOptions";

type Props = {
  validDate: boolean;
};

const filterToggleSx = {
  width: "100%",
  height: 48,
  minHeight: 48,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  px: 0,
  borderRadius: 0,
  textAlign: "left"
} as const;

const earlyInventoryGroupFilterSubProps = {
  toggleMoreItems: false,
  setToggleMoreItems: () => undefined,
  showDetailGroupFilter: false,
  filterName: "",
  filterMode: BookingFilterMode.FILTERING,
  isCapacityReportUsed: false,
  showQuantity: false,
  stackFilterItems: true
} as const;

function resolveCatalogBookingType(inputs: NotSerializedBookingInputs): BookingType | undefined {
  const mode = inputs.mode ?? inputs.bookingType;
  if (mode == null) {
    return undefined;
  }
  if (mode === BookingType.TEAM) {
    return BookingType.WORKPLACE;
  }
  if (mode === BookingType.VEHICLE || mode === BookingType.VISITOR) {
    return undefined;
  }
  return mode;
}

function buildPreferredPlaceCategoryFilterItems(
  preferredPlaceCategoryIds: readonly string[],
  placeCategoryEntries: { id: number; name: string }[]
): GroupFilterSelected[] {
  return preferredPlaceCategoryIds
    .map(id => Number(id))
    .map(id => placeCategoryEntries.find(category => category.id === id))
    .filter((category): category is NonNullable<typeof category> => category != null)
    .map(category => ({
      type: GroupFilterType.PLACECATEGORY,
      id: category.id,
      name: category.name,
      checked: true
    }));
}

const EARLY_FILTER_EXPAND_SCROLL_OFFSET_PX = 48;

function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function scrollElementIntoViewWithOffset(element: HTMLElement, offsetPx: number) {
  const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
  element.scrollIntoView({ behavior, block: "nearest" });
  window.scrollBy({ top: offsetPx, behavior });
}

export const FormDateTimeInventoryFilters: React.FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { inputs } = useSelector((state: RootState) => state.booking);
  const {
    settings: { preferredPlaceCategories }
  } = useSelector((state: RootState) => state.login);

  const [expanded, setExpanded] = useState(true);
  const [filterItems, setFilterItems] = useState<GroupFilterSelected[]>([]);
  const preferredDefaultsAppliedRef = useRef(false);
  const reduxHydratedRef = useRef(false);
  const expandPanelRef = useRef<HTMLDivElement>(null);
  const shouldScrollOnExpandRef = useRef(false);

  const sectionTitleId = useId();
  const expandPanelId = useId();

  const catalogsEnabled = shouldShowEarlyInventoryFilters(inputs);
  const catalogBookingType = resolveCatalogBookingType(inputs);

  const showPlaceCategories = placeModeExcludeVehicle(inputs) || inputs.mode === BookingType.TEAM;
  const showZoneCategories = zoneMode(inputs);

  const { data: filterOptions, isLoading: catalogsLoading } =
    useRemoteFetchBookingInventoryFilterOptions(catalogBookingType, {
      enabled: catalogsEnabled
    });

  const placeCategoryEntries = useMemo(
    () => (showPlaceCategories ? filterOptions?.placeCategories ?? [] : []),
    [filterOptions?.placeCategories, showPlaceCategories]
  );
  const zoneCategoryEntries = useMemo(
    () => (showZoneCategories ? filterOptions?.zoneCategories ?? [] : []),
    [filterOptions?.zoneCategories, showZoneCategories]
  );
  const bookingPropertyEntries = filterOptions?.bookingProperties ?? [];
  const equipmentCategories = filterOptions?.equipmentCategories ?? [];
  const equipmentEntries = filterOptions?.equipment ?? [];

  const activeFilterCount = useMemo(() => {
    const fromItems = countActiveGroupFilterItems(filterItems);
    if (fromItems > 0) {
      return fromItems;
    }
    return countActiveBookingFilterSelections(inputs.bookingFilters);
  }, [filterItems, inputs.bookingFilters]);

  const toggleAriaLabel =
    activeFilterCount > 0
      ? t("_booking_earlyInventoryFilter_toggleAriaWithCount", { count: activeFilterCount })
      : t("_booking_earlyInventoryFilter_expand");

  const applyPreferredDefaultsIfNeeded = useCallback(() => {
    if (
      preferredDefaultsAppliedRef.current ||
      hasActiveBookingFilters(inputs.bookingFilters) ||
      !showPlaceCategories ||
      preferredPlaceCategories.length === 0
    ) {
      preferredDefaultsAppliedRef.current = true;
      return;
    }

    if (placeCategoryEntries.length === 0) {
      return;
    }

    const preferredItems = buildPreferredPlaceCategoryFilterItems(
      preferredPlaceCategories,
      placeCategoryEntries
    );
    preferredDefaultsAppliedRef.current = true;

    if (preferredItems.length > 0) {
      setFilterItems(prev => {
        const withoutPlace = prev.filter(item => item.type !== GroupFilterType.PLACECATEGORY);
        return [...withoutPlace, ...preferredItems];
      });
    }
  }, [inputs.bookingFilters, placeCategoryEntries, preferredPlaceCategories, showPlaceCategories]);

  const handleToggleExpanded = useCallback(() => {
    setExpanded(prev => {
      if (!prev) {
        shouldScrollOnExpandRef.current = true;
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (!expanded || !shouldScrollOnExpandRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (!shouldScrollOnExpandRef.current) {
        return;
      }
      shouldScrollOnExpandRef.current = false;
      const panel = expandPanelRef.current;
      if (panel) {
        scrollElementIntoViewWithOffset(panel, EARLY_FILTER_EXPAND_SCROLL_OFFSET_PX);
      }
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [expanded]);

  useEffect(() => {
    if (!catalogsEnabled || catalogsLoading) {
      return;
    }

    applyPreferredDefaultsIfNeeded();
  }, [applyPreferredDefaultsIfNeeded, catalogsEnabled, catalogsLoading]);

  useLayoutEffect(() => {
    if (catalogsLoading) {
      return;
    }

    if (reduxHydratedRef.current) {
      return;
    }

    reduxHydratedRef.current = true;

    if (!hasActiveBookingFilters(inputs.bookingFilters)) {
      return;
    }

    setFilterItems(
      groupFilterItemsFromBookingFilters(inputs.bookingFilters!, {
        placeCategories: placeCategoryEntries,
        zoneCategories: zoneCategoryEntries,
        bookingProperties: bookingPropertyEntries,
        equipmentCategories,
        equipment: equipmentEntries
      })
    );
  }, [
    inputs.bookingFilters,
    placeCategoryEntries,
    zoneCategoryEntries,
    bookingPropertyEntries,
    equipmentCategories,
    equipmentEntries,
    catalogsLoading
  ]);

  useEffect(() => {
    if (!catalogsEnabled || catalogsLoading) {
      return;
    }

    const nextFilters = buildBookingFiltersFromGroupFilterItems(filterItems);
    if (hasActiveBookingFilters(nextFilters)) {
      dispatch(setBookingFilters(nextFilters));
      dispatch(
        setUserBookingFilters({
          userIndex: 0,
          bookingFilters: nextFilters
        })
      );
      return;
    }

    if (!reduxHydratedRef.current) {
      return;
    }

    dispatch(clearBookingFilters());
  }, [catalogsEnabled, catalogsLoading, dispatch, filterItems]);

  if (!shouldShowEarlyInventoryFilters(inputs)) {
    return null;
  }

  return (
    <Fade in={Boolean(inputsReady(inputs))} mountOnEnter unmountOnExit>
      <Box data-testid="form-dateTime-inventory-filters" sx={{ width: "100%" }}>
        <Grid2 container sx={{ width: "100%", borderTop: 1, borderColor: "divider", pt: 3, mt: 3 }}>
          <Grid2 size={{ xs: 12 }}>
            <Typography variant="h5" id={sectionTitleId}>
              {t("_booking_earlyInventoryFilter_title")}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12 }} sx={{ m: "1em" }}>
            <Stack spacing={1.5} role="group" aria-labelledby={sectionTitleId}>
              <Typography variant="body2" color="text.secondary">
                {t("_booking_earlyInventoryFilter_subtitle")}
              </Typography>
              <Box sx={{ width: "100%" }}>
                {catalogsLoading ? (
                  <Box
                    data-testid="early-inventory-filter-loading"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    aria-label={t("_booking_earlyInventoryFilter_loading")}
                    sx={{
                      ...filterToggleSx,
                      flexDirection: "column",
                      alignItems: "stretch",
                      justifyContent: "center",
                      gap: 1,
                      py: 0.5
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      <CircularProgress size={20} aria-hidden />
                      <Typography variant="body2" color="text.secondary">
                        {t("_booking_earlyInventoryFilter_loading")}
                      </Typography>
                    </Box>
                    <LinearProgress aria-hidden data-testid="early-inventory-filter-loading-bar" />
                  </Box>
                ) : (
                  <>
                    <ButtonBase
                      onClick={handleToggleExpanded}
                      aria-expanded={expanded}
                      aria-controls={expandPanelId}
                      aria-label={toggleAriaLabel}
                      data-testid="early-inventory-filter-summary"
                      sx={filterToggleSx}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Badge
                          badgeContent={activeFilterCount}
                          color="primary"
                          invisible={activeFilterCount === 0}
                          data-testid="early-inventory-filter-count-badge"
                          sx={{ mr: 1 }}
                        >
                          <FilterList aria-hidden sx={{ color: "text.primary" }} />
                        </Badge>
                        <Typography
                          variant="subtitle1"
                          sx={theme => ({
                            fontSize: "1.1em",
                            fontWeight: 700,
                            [theme.breakpoints.down("sm")]: {
                              fontSize: "1em"
                            }
                          })}
                          color="text.primary"
                        >
                          {t("_booking_earlyInventoryFilter_expand")}
                        </Typography>
                      </Box>
                      <ExpandMore
                        aria-hidden
                        data-testid="early-inventory-filter-expand-icon"
                        sx={theme => ({
                          color: "text.primary",
                          flexShrink: 0,
                          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: theme.transitions.create("transform", {
                            duration: theme.transitions.duration.shortest
                          }),
                          "@media (prefers-reduced-motion: reduce)": {
                            transition: "none"
                          }
                        })}
                      />
                    </ButtonBase>

                    <Collapse in={expanded} id={expandPanelId}>
                      <Box ref={expandPanelRef} sx={{ pt: 1, pb: 0, scrollMarginTop: 16 }}>
                        <GroupFiltersSub
                          {...earlyInventoryGroupFilterSubProps}
                          type={GroupFilterType.EQUIPMENT}
                          entries={equipmentEntries}
                          filterItems={filterItems}
                          setFilterItems={setFilterItems}
                        />

                        <GroupFiltersSub
                          {...earlyInventoryGroupFilterSubProps}
                          type={GroupFilterType.EQUIPMENTCATEGORY}
                          entries={equipmentCategories}
                          filterItems={filterItems}
                          setFilterItems={setFilterItems}
                        />

                        {showPlaceCategories && (
                          <GroupFiltersSub
                            {...earlyInventoryGroupFilterSubProps}
                            type={GroupFilterType.PLACECATEGORY}
                            entries={placeCategoryEntries}
                            filterItems={filterItems}
                            setFilterItems={setFilterItems}
                          />
                        )}

                        {showZoneCategories && (
                          <GroupFiltersSub
                            {...earlyInventoryGroupFilterSubProps}
                            type={GroupFilterType.ZONECATEGORY}
                            entries={zoneCategoryEntries}
                            filterItems={filterItems}
                            setFilterItems={setFilterItems}
                          />
                        )}

                        <GroupFiltersSub
                          {...earlyInventoryGroupFilterSubProps}
                          type={GroupFilterType.BOOKINGPROPERTY}
                          entries={bookingPropertyEntries}
                          filterItems={filterItems}
                          setFilterItems={setFilterItems}
                        />
                      </Box>
                    </Collapse>
                  </>
                )}
              </Box>
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
    </Fade>
  );
};

export default FormDateTimeInventoryFilters;
